import React from 'react';
import { Card, CardContent, Box, Typography, Avatar, CircularProgress } from '@mui/material';
import CountUp from 'react-countup';

interface EnhancedStatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
  isPercentage?: boolean;
  percentageValue?: number;
}

const EnhancedStatCard: React.FC<EnhancedStatCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  isPercentage,
  percentageValue,
}) => {
  // Parsear el valor numérico para CountUp
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
  const isValidNumber = !isNaN(numericValue);
  const suffix = typeof value === 'string' && value.includes('%') ? '%' : '';

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        minHeight: 180,
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px)',
          transition: 'all 0.3s ease-in-out',
        },
        transition: 'all 0.3s ease-in-out',
      }}
    >
      {/* Franja de color superior */}
      <Box sx={{ height: 4, bgcolor: color, borderRadius: '4px 4px 0 0' }} />

      {/* Gradiente de fondo sutil */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '120px',
          height: '120px',
          background: `radial-gradient(circle, ${color}12 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <CardContent
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2.5,
          '&:last-child': { pb: 2.5 },
        }}
      >
        {/* Fila superior: icono + título — altura fija */}
        <Box display="flex" alignItems="center" gap={1.5} sx={{ minHeight: 42 }}>
          <Avatar sx={{ bgcolor: `${color}20`, width: 42, height: 42, flexShrink: 0, boxShadow: 'none' }}>
            <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          </Avatar>
          <Typography variant="body2" color="textSecondary" fontWeight={600} lineHeight={1.2}>
            {title}
          </Typography>
        </Box>

        {/* Valor central — siempre en la misma posición (flex-grow para ocupar el espacio disponible) */}
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flex: 1, py: 1.5 }}>
          <Box>
            {isValidNumber ? (
              <Typography variant="h3" component="div" fontWeight="bold" color="text.primary" lineHeight={1}>
                <CountUp
                  end={numericValue}
                  duration={1.5}
                  decimals={suffix === '%' ? 1 : 0}
                  suffix={suffix}
                  separator=","
                />
              </Typography>
            ) : (
              <Typography variant="h3" component="div" fontWeight="bold" color="text.primary" lineHeight={1}>
                {value}
              </Typography>
            )}
          </Box>

          {/* Indicador circular para porcentajes */}
          {isPercentage && percentageValue !== undefined && (
            <Box position="relative" display="inline-flex">
              <CircularProgress
                variant="determinate"
                value={Math.min(percentageValue, 100)}
                size={52}
                thickness={4}
                sx={{
                  color: percentageValue >= 80 ? '#4caf50' : percentageValue >= 60 ? '#ff9800' : '#f44336',
                }}
              />
              <CircularProgress
                variant="determinate"
                value={100}
                size={52}
                thickness={4}
                sx={{
                  color: 'divider',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: -1,
                }}
              />
              <Box
                sx={{
                  top: 0, left: 0, bottom: 0, right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" component="div" fontWeight="bold" fontSize="0.65rem">
                  {`${Math.round(percentageValue)}%`}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Pie: trend — altura fija siempre reservada para simetría entre cards */}
        <Box sx={{ minHeight: 20 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', visibility: trend ? 'visible' : 'hidden' }}>
            {trend || '.'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EnhancedStatCard;
